import { isEmpty } from 'lodash';
import i18next from 'i18next';
import React from 'react';

import { getClientStoreNameTranslation } from '@commons/utils/translations';
import { Label } from '@commons/utils/styledLibraryComponents';
import DisplayNumber from '@commons/DisplayNumber';

import theme from '@theme';

import { Text } from '../components/styledComponents';

import { getCompositionLabel } from './getProductExportLabels';

const getCashierMappedColor = (product) => {
  if (!product) {
    return null;
  }
  if (product.nbCashierProductMappingCount > 0 && product.nbCashierProductMappingActive > 0) {
    return theme.colors.infoGreen;
  }
  if (product.nbCashierProductMappingCount > 0 && product.nbCashierProductMappingActive === 0) {
    return theme.colors.infoOrange;
  }
  if (product.nbCashierProductMappingCount === 0) {
    return theme.colors.infoRed;
  }
};

export const getColumns = (client) => {
  const columns = [
    {
      id: 'productName',
      name: i18next.t('GENERAL.NAME'),
      displayName: i18next.t('GENERAL.NAME'),
      propertyKey: 'name',
      baseName: 'name',
      large: true,
      render: (item) => <Text highlighted>{item}</Text>,
    },
    {
      id: 'sku',
      baseName: 'sku',
      propertyKey: 'sku',
      displayName: i18next.t('ORDERS.ORDERS.LIST_LABEL_SKU'),
      name: i18next.t('ORDERS.ORDERS.LIST_LABEL_SKU'),
      minWidth: 130,
      render: (item) => <Text>{item ? item : '-'}</Text>,
    },
    {
      id: 'category',
      baseName: 'category',
      propertyKey: 'categoryId',
      displayName: i18next.t('ADMIN.SUPPLIERS.LIST_COLUMN_CATEGORY'),
      name: i18next.t('ADMIN.SUPPLIERS.LIST_COLUMN_CATEGORY'),
      filterType: 'string',
      filterStringAllowNone: true,
      render: (_, { category }) => <Text>{category || '-'}</Text>,
    },
    {
      id: 'subCategory',
      baseName: 'subCategory',
      propertyKey: 'subCategoryId',
      displayName: i18next.t('ADMIN.STORES.LIST_COLUMN_SUB_CATEGORY'),
      name: i18next.t('ADMIN.STORES.LIST_COLUMN_SUB_CATEGORY'),
      filterType: 'string',
      minWidth: 130,
      render: (_, { subCategory }) => <Text>{subCategory || '-'}</Text>,
    },
    {
      id: 'priceTaxes',
      baseName: 'priceWithTaxes',
      propertyKey: 'priceWithTaxes',
      displayName: i18next.t('FORECAST.PRODUCT_MIX.LIST_HEADER_PRICE_LABEL'),
      name: i18next.t('FORECAST.PRODUCT_MIX.LIST_HEADER_PRICE_LABEL'),
      filterType: 'numeric',
      tooltipText:
        client.hasMultipleChannels && i18next.t('ADMIN.PRODUCTS.PRICE_WITH_TAXES_TOOLTIP'),
      render: (item) => <DisplayNumber displayCurrencyCode={true} number={item} />,
    },
    {
      id: 'composition',
      baseName: 'composition',
      propertyKey: 'isIngredient',
      displayName: i18next.t('ADMIN.RECIPES.EXPORT_COLUMN_PRODUCT_COMPOSITION'),
      name: i18next.t('ADMIN.RECIPES.EXPORT_COLUMN_PRODUCT_COMPOSITION'),
      filterType: 'string',
      render: (_, { entity }) => <Text>{getCompositionLabel(entity)}</Text>,
    },
  ];

  if (client.hasMultipleBrands) {
    columns.splice(2, 0, {
      id: 'brand',
      name: i18next.t('GENERAL.BRAND'),
      displayName: i18next.t('GENERAL.BRAND'),
      propertyKey: 'brandId',
      baseName: 'brand',
      disableSort: true,
      filterType: 'string',
      render: (_, { brand }) => <Text>{brand || '-'}</Text>,
    });
  }

  if (client.areProductsSync) {
    columns.push({
      id: 'cashierProducts',
      baseName: 'nbCashierProductMappingCount',
      propertyKey: 'nbCashierProductMappingCount',
      displayName: i18next.t('ADMIN.PRODUCTS.CASHIER_PRODUCT_ASSOCIATE'),
      name: i18next.t('ADMIN.PRODUCTS.CASHIER_PRODUCT_ASSOCIATE'),
      filterType: 'numeric',
      disableSort: true,
      minWidth: 180,
      render: (item, product) => {
        const color = getCashierMappedColor(product);
        return (
          <Label
            background={color}
            children={item}
            color={color}
            height={'24px'}
            type={'outline'}
            width={'24px'}
          />
        );
      },
    });
  }

  return columns;
};

export const getCentralKitchenProductsColumns = (clientStoreName) => {
  const clientStoreNamePlural = getClientStoreNameTranslation(clientStoreName, true);

  return [
    {
      id: 'productName',
      name: i18next.t('GENERAL.NAME'),
      propertyKey: 'name',
      filterType: 'string',
    },
    {
      id: 'category',
      propertyKey: 'categoryId',
      name: i18next.t('ADMIN.SUPPLIERS.LIST_COLUMN_CATEGORY'),
      filterType: 'string',
      filterStringAllowNone: true,
      render: (_, { category }) => <Text>{category || '-'}</Text>,
    },
    {
      id: 'subCategory',
      propertyKey: 'subCategoryId',
      name: i18next.t('ADMIN.STORES.LIST_COLUMN_SUB_CATEGORY'),
      filterType: 'string',
      minWidth: 130,
      render: (_, { subCategory }) => <Text>{subCategory || '-'}</Text>,
    },
    {
      id: 'priceWithTaxes',
      propertyKey: 'priceWithTaxes',
      name: i18next.t('FORECAST.PRODUCT_MIX.LIST_HEADER_PRICE_LABEL'),
      filterType: 'numeric',
      render: (item) => <DisplayNumber displayCurrencyCode={true} number={item} />,
    },
    {
      id: 'composition',
      propertyKey: 'isIngredient',
      name: i18next.t('ADMIN.RECIPES.EXPORT_COLUMN_PRODUCT_COMPOSITION'),
      filterType: 'string',
      render: (_, { entity }) => <Text>{getCompositionLabel(entity)}</Text>,
    },
    {
      id: 'deliveredStoreCount',
      disableSort: true,
      propertyKey: 'deliveredStoreCount',
      name: i18next.t('ADMIN.CENTRAL_PRODUCTS.DELIVERED_STORE', {
        storeName: clientStoreNamePlural,
      }),
      filterType: 'numeric',
      render: (item) => {
        const color = item === 0 ? theme.colors.red : theme.colors.green.light;

        return (
          <Label
            background={color}
            children={item}
            color={color}
            height={'24px'}
            type={'outline'}
            width={'24px'}
          />
        );
      },
    },
  ];
};

export const getColumnsFilterList = (
  columns,
  isInCentralKitchenOnlyPage,
  categories,
  subCategories,
  clientBrands,
) => {
  let columnsList = [...columns];

  if (isInCentralKitchenOnlyPage) {
    columnsList = columnsList.filter(
      ({ propertyKey }) => !['name', 'composition'].includes(propertyKey),
    );
  }

  return columnsList.reduce((result, column) => {
    if (!column.filterType) {
      return result;
    }

    // Due to embed label of associated cashierProducts do this to map to the value for filter
    if (column.id === 'cashierProducts') {
      column.propertyKey = 'nbCashierProductMappingCount';
    }

    const item = { ...column };

    if (column.filterType === 'string' && column.propertyKey === 'categoryId') {
      const choices = categories.reduce((acc, category, index) => {
        acc.push({
          id: index,
          name: category.name,
          categoryId: category.id,
        });

        return acc;
      }, []);

      if (isEmpty(choices)) {
        return result;
      }

      item.list = choices;
    }

    if (column.filterType === 'string' && column.propertyKey === 'subCategoryId') {
      const choices = subCategories.reduce((acc, subCategory, index) => {
        acc.push({
          id: index,
          name: subCategory.name,
          subCategoryId: subCategory.id,
        });

        return acc;
      }, []);

      if (isEmpty(choices)) {
        return result;
      }

      item.list = choices;
    }

    if (column.filterType === 'string' && column.propertyKey === 'brandId') {
      const choices = clientBrands.reduce((acc, brand, index) => {
        acc.push({
          id: index,
          name: brand.name,
          brandId: brand.id,
        });

        return acc;
      }, []);

      if (isEmpty(choices)) {
        return result;
      }

      item.list = choices;
    }

    if (column.filterType === 'string' && column.propertyKey === 'isIngredient') {
      const choices = [
        {
          id: 0,
          name: i18next.t('GENERAL.NONE_VALUE'),
          isIngredient: undefined,
        },
        {
          id: 1,
          name: i18next.t('GENERAL.INGREDIENT'),
          isIngredient: true,
        },
        {
          id: 2,
          name: i18next.t('GENERAL.RECIPE'),
          isIngredient: false,
        },
      ];

      item.list = choices;
    }

    result.push(item);

    return result;
  }, []);
};

export default { getColumns, getCentralKitchenProductsColumns, getColumnsFilterList };
